export const getNestedValue = (obj, path, defaultValue = false) => {
  if (!obj || typeof obj !== "object") {
    return defaultValue;
  }

  const keys = path.split(".");
  let result = obj;

  for (const key of keys) {
    if (result === undefined || result === null || typeof result !== "object") {
      return defaultValue;
    }
    result = result[key];
  }

  return result === undefined ? defaultValue : result;
};

// Normalize any permission-ish value to a strict boolean.
// This prevents objects, numbers, or unexpected truthy strings from granting access.
const normalizePermissionValue = (value, defaultValue = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "y"].includes(normalized)) return true;
    if (["false", "0", "no", "n"].includes(normalized)) return false;
  }
  return defaultValue;
};

export const checkPermission = (
  userPermissions,
  permissionPath,
  defaultValue = false
) => {
  if (!userPermissions || typeof userPermissions !== "object") {
    return defaultValue;
  }

  const result = getNestedValue(userPermissions, permissionPath, defaultValue);
  return normalizePermissionValue(result, defaultValue);
};

// Dashboard Permissions
export const canViewDashboard = (permissions) =>
  checkPermission(
    permissions,
    "dashboard_details.update_dashboard_details.view_dashboard"
  ) || checkPermission(permissions, "dashboard_details.full_access");

export const canViewAdminBar = (permissions) =>
  checkPermission(
    permissions,
    "dashboard_details.update_dashboard_details.view_admin_bar"
  ) || checkPermission(permissions, "dashboard_details.full_access");

export const canViewCenterOnboarders = (permissions) =>
  checkPermission(
    permissions,
    "center_onboard_details.update_center_onboard_details.view_center_onboard"
  ) || checkPermission(permissions, "center_onboard_details.full_access");

// Technician Permissions
export const canViewTechnicians = (permissions) =>
  checkPermission(
    permissions,
    "technician_details.update_technician_details.view_technician"
  ) || checkPermission(permissions, "technician_details.full_access");

// Wallet Permissions
const hasWalletFullAccess = (permissions) =>
  checkPermission(permissions, "transaction_wallet_details.full_access");

export const canViewCenterTransactions = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_center_transaction"
  ) || hasWalletFullAccess(permissions);

export const canViewConsumerTransactions = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_consumer_transaction"
  ) || hasWalletFullAccess(permissions);

export const canViewWallet = (permissions) =>
  canViewCenterTransactions(permissions) ||
  canViewConsumerTransactions(permissions) ||
  canViewUserTransaction(permissions) ||
  canViewClientTransaction(permissions) ||
  canClientRecharge(permissions) ||
  canConsumerTechnicianRecharge(permissions) ||
  hasWalletFullAccess(permissions);

// Notification Permissions
export const canViewNotifications = (permissions) =>
  checkPermission(
    permissions,
    "notification_details.update_notification_details.view_notification"
  ) || checkPermission(permissions, "notification_details.full_access");

export const canAddNotification = (permissions) =>
  checkPermission(
    permissions,
    "notification_details.update_notification_details.add_notification"
  ) || checkPermission(permissions, "notification_details.full_access");

export const canSendNotification = (permissions) =>
  checkPermission(
    permissions,
    "notification_details.update_notification_details.send_notification"
  ) || checkPermission(permissions, "notification_details.full_access");

export const canLogNotification = (permissions) =>
  checkPermission(
    permissions,
    "notification_details.update_notification_details.log_notification"
  ) || checkPermission(permissions, "notification_details.full_access");

export const canToggleNotificationStatus = (permissions) =>
  checkPermission(
    permissions,
    "notification_details.update_notification_details.active_inactive"
  ) || checkPermission(permissions, "notification_details.full_access");

export const canAccessNotificationFull = (permissions) =>
  checkPermission(permissions, "notification_details.full_access");

// Service Permissions (Fixed - added proper parameter usage)
export const canViewServices = (permissions) =>
  checkPermission(
    permissions,
    "services_details.update_services_details.primary_services.view_services_list"
  ) || checkPermission(permissions, "services_details.full_primary_access");

export const canAddPrimaryService = (permissions) =>
  checkPermission(
    permissions,
    "services_details.update_services_details.primary_services.add_services"
  ) || checkPermission(permissions, "services_details.full_primary_access");

export const canUpdatePrimaryService = (permissions) =>
  checkPermission(
    permissions,
    "services_details.update_services_details.primary_services.update_services"
  ) || checkPermission(permissions, "services_details.full_primary_access");

export const canDeletePrimaryService = (permissions) =>
  checkPermission(
    permissions,
    "services_details.update_services_details.primary_services.inactive_active"
  ) || checkPermission(permissions, "services_details.full_primary_access");

export const canViewClientServices = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

export const canAddClientService = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

export const canUpdateClientService = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

export const canDeleteClientService = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

export const canViewTicketAcceptance = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

export const canManageTicketAcceptance = (permissions) =>
  checkPermission(permissions, "services_details.full_client_access");

// Center Permissions

export const canViewCenter = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_center"
  ) || checkPermission(permissions, "center_details.full_access");

export const canViewCenterDetails = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_center"
  ) || checkPermission(permissions, "center_details.full_access");

export const canAddCenter = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.add_center"
  ) || checkPermission(permissions, "center_details.full_access");

export const canUpdateCenter = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.update_center"
  ) || checkPermission(permissions, "center_details.full_access");

export const canDeleteCenter = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.delete_center"
  ) || checkPermission(permissions, "center_details.full_access");

export const canViewPincode = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_pincode"
  ) || checkPermission(permissions, "center_details.full_access");

export const canViewNumber = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_number"
  ) || checkPermission(permissions, "center_details.full_access");

export const canDefineCapacity = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.define_capacity"
  ) || checkPermission(permissions, "center_details.full_access");

export const canAuthorizeClient = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.authorize_client"
  ) || checkPermission(permissions, "center_details.full_access");

export const canViewCenterHistory = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_center_history"
  ) || checkPermission(permissions, "center_details.full_access");

export const canViewDocuments = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.view_documents"
  ) || checkPermission(permissions, "center_details.full_access");

export const canDownloadCenterList = (permissions) =>
  checkPermission(
    permissions,
    "center_details.update_center_details.download_center_list"
  ) || checkPermission(permissions, "center_details.full_access");

// Ticket Permissions
export const hasFullAccess = (permissions) =>
  checkPermission(permissions, "ticket_details.full_normal_access");

const withTicketFullAccess = (permissions, path) =>
  checkPermission(permissions, path) || hasFullAccess(permissions);

export const canViewTickets = (permissions) =>
  hasFullAccess(permissions) ||
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.view_normal_ticket"
  );

export const canViewAllTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_all_ticket"
  );

export const canViewNormalTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_normal_ticket"
  );

export const canViewPendingTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_pending_ticket"
  );

export const canViewClosedTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_closed_ticket"
  );

export const canViewNoServiceTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_no_service_ticket"
  );

export const canViewCenterTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_center_ticket"
  );

export const canViewERickshawTickets = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_e_rikshaw_ticket"
  );

export const canUpdateTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.update_ticket"
  );

export const canDeleteTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.delete_ticket"
  );

export const canAssignUnassignTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.assigned_notassigned_ticket"
  );

export const canBroadcastTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.broadcast_ticket"
  );

export const canDownloadTicketList = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.download_ticket_list"
  );

export const canRemarkTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.remark_ticket"
  );

export const canViewContactNumber = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.view_number"
  );

export const canCloseTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.closed_ticket"
  );

// Client Permissions
export const canViewClients = (permissions) =>
  checkPermission(
    permissions,
    "clients_details.update_clients_details.view_clients"
  ) || checkPermission(permissions, "clients_details.full_access");

export const canAddClients = (permissions) =>
  checkPermission(
    permissions,
    "clients_details.update_clients_details.add_clients"
  ) || checkPermission(permissions, "clients_details.full_access");

export const canUpdateClients = (permissions) =>
  checkPermission(
    permissions,
    "clients_details.update_clients_details.update_clients"
  ) || checkPermission(permissions, "clients_details.full_access");

export const canDeleteClients = (permissions) =>
  checkPermission(
    permissions,
    "clients_details.update_clients_details.delete_clients"
  ) || checkPermission(permissions, "clients_details.full_access");

export const canViewPincodeClient = (permissions) =>
  checkPermission(
    permissions,
    "clients_details.update_clients_details.view_pincode"
  ) || checkPermission(permissions, "clients_details.full_access");

export const hasFullClientAccess = (permissions) =>
  checkPermission(permissions, "clients_details.full_access");

// Ticket Permissions - Add these to your existing permissionUtility.js

export const canAddTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.add_ticket"
  );

export const canBulkUploadTicket = (permissions) =>
  withTicketFullAccess(
    permissions,
    "ticket_details.update_ticket_details.bulk_ticket_upload"
  );

// User Permissions
export const canAddUser = (permissions) =>
  checkPermission(permissions, "user_details.update_user_details.add_user") ||
  checkPermission(permissions, "user_details.full_access");

export const canUpdateUser = (permissions) =>
  checkPermission(
    permissions,
    "user_details.update_user_details.update_user"
  ) || checkPermission(permissions, "user_details.full_access");

export const canViewUsers = (permissions) =>
  checkPermission(permissions, "user_details.update_user_details.view_user") ||
  checkPermission(permissions, "user_details.full_access");

export const canActivateDeactivateUser = (permissions) =>
  checkPermission(
    permissions,
    "user_details.update_user_details.active_inactive"
  ) || checkPermission(permissions, "user_details.full_access");

export const hasFullUserAccess = (permissions) =>
  checkPermission(permissions, "user_details.full_access");

// Manual Wallet Permissions
export const canViewUserTransaction = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_manual_wallet_adjustment.view_user_transaction"
  ) || checkPermission(permissions, "transaction_wallet_details.full_access");

export const canViewClientTransaction = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_manual_wallet_adjustment.view_client_transaction"
  ) || checkPermission(permissions, "transaction_wallet_details.full_access");

export const canClientRecharge = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_manual_wallet_adjustment.client_recharge"
  ) || checkPermission(permissions, "transaction_wallet_details.full_access");

export const canConsumerTechnicianRecharge = (permissions) =>
  checkPermission(
    permissions,
    "transaction_wallet_details.update_transaction_wallet_details.view_manual_wallet_adjustment.consumer_technician_recharge"
  ) || checkPermission(permissions, "transaction_wallet_details.full_access");

// Banner Permissions
export const canViewBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.consumer_banner.view_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canActivateDeactivateConsumerBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.consumer_banner.active_inactive_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canDeleteConsumerBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.consumer_banner.delete_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canUploadConsumerBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.consumer_banner.upload_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canViewClientCenterBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.client_center_banner.view_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canActivateDeactivateClientCenterBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.client_center_banner.active_inactive_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canDeleteClientCenterBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.client_center_banner.delete_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const canUploadClientCenterBanner = (permissions) =>
  checkPermission(
    permissions,
    "banner_details.update_banner_details.client_center_banner.upload_banner"
  ) || checkPermission(permissions, "banner_details.full_access");

export const hasFullBannerAccess = (permissions) =>
  checkPermission(permissions, "banner_details.full_access");

// Offers Permissions
export const canViewOffers = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.offer.view_offer"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canAddOffer = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.offer.add_offer"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canUpdateOffer = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.offer.update_offer"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canDeleteOffer = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.offer.delete_offer"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canActiveInactiveOffer = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.offer.active_inactive_offer"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

// Discounts Permissions
export const canViewDiscounts = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.discount.view_discount"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canAddDiscount = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.discount.add_discount"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canUpdateDiscount = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.discount.update_discount"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canDeleteDiscount = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.discount.delete_discount"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

export const canActiveInactiveDiscount = (permissions) =>
  checkPermission(
    permissions,
    "offer_discount_details.update_offer_details.discount.active_inactive_discount"
  ) || checkPermission(permissions, "offer_discount_details.full_access");

// Subscription Permissions
export const canViewSubscription = (permissions) =>
  checkPermission(
    permissions,
    "subscription_details.subscription_details.view_subscription"
  ) || checkPermission(permissions, "subscription_details.full_access");

export const canUpdateSubscription = (permissions) =>
  checkPermission(
    permissions,
    "subscription_details.subscription_details.update_subscription"
  ) || checkPermission(permissions, "subscription_details.full_access");

export const hasFullSubscriptionAccess = (permissions) =>
  checkPermission(permissions, "subscription_details.full_access");

// Blog Permissions
export const canViewBlog = (permissions) =>
  checkPermission(permissions, "blog_details.blog_details.view_blog") ||
  checkPermission(permissions, "blog_details.full_access");

export const canAddBlog = (permissions) =>
  checkPermission(permissions, "blog_details.blog_details.add_blog") ||
  checkPermission(permissions, "blog_details.full_access");

export const canUpdateBlog = (permissions) =>
  checkPermission(permissions, "blog_details.blog_details.update_blog") ||
  checkPermission(permissions, "blog_details.full_access");

export const canDeleteBlog = (permissions) =>
  checkPermission(permissions, "blog_details.blog_details.delete_blog") ||
  checkPermission(permissions, "blog_details.full_access");

export const hasFullBlogAccess = (permissions) =>
  checkPermission(permissions, "blog_details.full_access");

// Role Permissions
export const canViewRoles = (permissions) =>
  checkPermission(permissions, "role_details.update_role_details.view_role") ||
  checkPermission(permissions, "role_details.full_access");

export const canAddRole = (permissions) =>
  checkPermission(permissions, "role_details.update_role_details.add_role") ||
  checkPermission(permissions, "role_details.full_access");

export const canUpdateRole = (permissions) =>
  checkPermission(
    permissions,
    "role_details.update_role_details.update_role"
  ) || checkPermission(permissions, "role_details.full_access");

export const hasFullRoleAccess = (permissions) =>
  checkPermission(permissions, "role_details.full_access");

// Ticket Permissions - Extended
export const canCancelTicket = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.cancel_ticket"
  ) || hasFullAccess(permissions);

export const canPartRequestTicket = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.part_request_ticket"
  ) || hasFullAccess(permissions);

export const canViewPartRequestTicketList = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.part_request_ticket_list"
  ) || hasFullAccess(permissions);

export const canPutOnHoldTicket = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.put_on_hold_ticket"
  ) || hasFullAccess(permissions);

export const canRefundTicketAmount = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.refund_ticket_amount"
  ) || hasFullAccess(permissions);

export const canUpdateSpecificRequirement = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.specific_requirement_ticket"
  ) || hasFullAccess(permissions);

export const canViewBroadcastedList = (permissions) =>
  checkPermission(
    permissions,
    "ticket_details.update_ticket_details.view_broadcasted_list"
  ) || hasFullAccess(permissions);
